import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormHelperText,
  Grid, 
  Paper, 
  Typography,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// Styled components
const UploadBox = styled(Box)(({ theme, isDragActive, error }) => ({
  border: `2px dashed ${error ? theme.palette.error.main : 
    isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isDragActive ? 
    theme.palette.mode === 'dark' ? 'rgba(0, 127, 255, 0.08)' : 'rgba(0, 127, 255, 0.04)' : 
    theme.palette.background.default,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color']),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 127, 255, 0.08)' : 'rgba(0, 127, 255, 0.04)',
  },
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const FilePreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  marginBottom: theme.spacing(1),
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  '& .delete-button': {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: theme.transitions.create('opacity'),
  },
  '&:hover .delete-button': {
    opacity: 1,
  },
}));

/**
 * Get the appropriate icon based on file type
 */
const getFileIcon = (fileType) => {
  if (fileType.startsWith('image/')) return <ImageIcon />;
  if (fileType.startsWith('video/')) return <MovieIcon />;
  if (fileType === 'application/pdf') return <PictureAsPdfIcon />;
  return <InsertDriveFileIcon />;
};

/**
 * MagicFileUpload - Enhanced file upload component with drag and drop, preview
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.multiple - Whether multiple files can be selected
 * @param {string} props.accept - File types to accept (e.g., "image/*,video/*")
 * @param {boolean} props.showPreview - Whether to show file previews
 * @param {boolean} props.isLoading - Whether upload is in progress
 * @param {Array|File} props.value - Current file(s) value
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {string} props.label - Field label
 * @param {Object} props.sx - Additional styles
 */
const MagicFileUpload = ({
  name,
  onChange,
  multiple = false,
  accept,
  showPreview = true,
  isLoading = false,
  value,
  error = false,
  helperText = '',
  label = 'Upload Files',
  sx = {},
  ...props
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  // Convert value to array for consistent handling
  const files = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Handle file input change
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  // Process selected files
  const handleFiles = (fileList) => {
    const newFiles = multiple ? 
      [...files, ...Array.from(fileList)] : 
      [fileList[0]];
    
    if (onChange) {
      onChange(multiple ? newFiles : newFiles[0], name);
    }
  };

  // Handle file removal
  const handleRemoveFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    
    if (onChange) {
      onChange(multiple ? newFiles : newFiles[0] || null, name);
    }
  };

  // Handle button click
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Render file previews
  const renderPreviews = () => {
    if (!showPreview || files.length === 0) return null;
    
    return (
      <PreviewContainer>
        <Grid container spacing={2}>
          {files.map((file, index) => {
            // Handle URLs and File objects differently
            const isUrl = typeof file === 'string';
            const isImageUrl = isUrl && (
              file.match(/\.(jpeg|jpg|gif|png)$/) !== null || 
              file.includes('image')
            );
            const isImageFile = !isUrl && file.type?.startsWith('image/');
            
            // For image files and URLs
            if (isImageUrl || isImageFile) {
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <ImagePreview>
                    <img 
                      src={isUrl ? file : URL.createObjectURL(file)}
                      alt={file.name || `File ${index + 1}`}
                      style={{ 
                        width: '100%', 
                        height: 140, 
                        objectFit: 'cover',
                        borderRadius: 8 
                      }}
                    />
                    <IconButton
                      className="delete-button"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      aria-label="remove file"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ImagePreview>
                </Grid>
              );
            } 
            
            // For non-image files
            return (
              <Grid item xs={12} key={index}>
                <FilePreview>
                  {isUrl ? (
                    <InsertDriveFileIcon sx={{ mr: 1 }} />
                  ) : (
                    getFileIcon(file.type)
                  )}
                  <Typography variant="body2" sx={{ flex: 1, ml: 1 }}>
                    {isUrl ? file.split('/').pop() : file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    aria-label="remove file"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </FilePreview>
              </Grid>
            );
          })}
        </Grid>
      </PreviewContainer>
    );
  };

  return (
    <Box sx={{ ...sx }}>
      <input
        ref={fileInputRef}
        type="file"
        id={name}
        name={name}
        onChange={handleInputChange}
        multiple={multiple}
        accept={accept}
        style={{ display: 'none' }}
        {...props}
      />
      
      <UploadBox
        isDragActive={isDragActive}
        error={error}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <CloudUploadIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {multiple ? 
                'Drag & drop files here, or click to select files' : 
                'Drag & drop a file here, or click to select a file'}
            </Typography>
            {accept && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Accepted file types: {accept.replace(/\./g, '').replace(/,/g, ', ')}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClick();
              }}
            >
              Browse Files
            </Button>
          </>
        )}
      </UploadBox>
      
      {error && helperText && (
        <FormHelperText error>{helperText}</FormHelperText>
      )}
      
      {renderPreviews()}
    </Box>
  );
};

export default MagicFileUpload;